import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, AlertCircle } from 'lucide-react';
import { SubscriptionPlan } from '@/types/subscriptionPlans';
import { PlanType } from '@/hooks/useAdminPlans';

interface PlanCardProps {
  plan: SubscriptionPlan;
  selectedPlanId: string | null;
  selectedPlanType: PlanType;
  onSelectPlan: (planId: string, planType: PlanType) => void;
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  selectedPlanId,
  selectedPlanType,
  onSelectPlan,
}) => {
  const getCardColors = (planId: string) => {
    switch (planId) {
      case 'starter':
        return {
          borderColor: 'border-blue-200 hover:border-blue-500',
          badge: 'bg-blue-500',
          gradient: 'bg-gradient-to-b from-white to-blue-50',
          title: 'text-blue-700',
          button: 'bg-blue-600 hover:bg-blue-700'
        };
      case 'growth':
        return {
          borderColor: 'border-green-200 hover:border-green-500',
          badge: 'bg-green-500',
          gradient: 'bg-gradient-to-b from-white to-green-50',
          title: 'text-green-700',
          button: 'bg-green-600 hover:bg-green-700'
        };
      case 'professional':
        return {
          borderColor: 'border-purple-200 hover:border-purple-500',
          badge: 'bg-purple-500',
          gradient: 'bg-gradient-to-b from-white to-purple-50',
          title: 'text-purple-700',
          button: 'bg-purple-600 hover:bg-purple-700'
        };
      case 'enterprise':
        return {
          borderColor: 'border-indigo-200 hover:border-indigo-500',
          badge: 'bg-indigo-500',
          gradient: 'bg-gradient-to-b from-white to-indigo-50',
          title: 'text-indigo-700',
          button: 'bg-indigo-600 hover:bg-indigo-700'
        };
      default:
        return {
          borderColor: 'border-gray-200 hover:border-gray-500',
          badge: 'bg-gray-500',
          gradient: 'bg-gradient-to-b from-white to-gray-50',
          title: 'text-gray-700',
          button: 'bg-gray-600 hover:bg-gray-700'
        };
    }
  };

  const colors = getCardColors(plan.id);
  
  // Get the price based on the selected billing period
  const getPrice = () => {
    switch (selectedPlanType) {
      case 'monthly':
        return plan.monthlyPrice;
      case 'quarterly':
        return plan.quarterlyPrice;
      case 'yearly':
        return plan.yearlyPrice || Math.round(plan.monthlyPrice * 12 * 0.8); // 20% discount if not specified
      default:
        return plan.monthlyPrice;
    }
  };
  
  const price = getPrice();
  
  return (
    <Card 
      className={`relative transform transition-all duration-300 ${
        selectedPlanId === plan.id 
          ? `border-2 ring-2 ring-opacity-20 ${colors.borderColor} scale-105`
          : `border ${colors.borderColor} hover:scale-105`
      } transition-all ${colors.gradient} shadow-md`}
    >
      {plan.recommended && (
        <div className={`absolute top-0 right-0 ${colors.badge} text-white px-3 py-1 text-xs font-semibold rounded-bl-md rounded-tr-md shadow-sm`}>
          {plan.badgeText || 'RECOMMENDED'}
        </div>
      )}
      <CardHeader className="pb-2">
        <CardTitle className={`text-lg ${colors.title}`}>{plan.name}</CardTitle>
        <CardDescription>
          <div className="mt-1 text-xl font-bold text-gray-800">
            ${price}
            <span className="text-sm font-normal text-gray-500">
              /{selectedPlanType === 'monthly' ? 'month' : selectedPlanType === 'quarterly' ? 'quarter' : 'year'}
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-600">{plan.description}</p>
        </CardDescription>
      </CardHeader>
      <CardContent className="pb-2">
        <div className="space-y-1.5">
          {plan.features.slice(0, 4).map((feature, idx) => (
            <div key={idx} className="flex items-start">
              {feature.included ? (
                <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="h-4 w-4 text-gray-300 mr-2 flex-shrink-0 mt-0.5" />
              )}
              <span className={`text-xs ${feature.included ? 'text-gray-700' : 'text-gray-400'}`}>
                {feature.name}
                {feature.limit ? ` (${feature.limit})` : ''}
              </span>
            </div>
          ))}
          <div className="pt-2 border-t border-gray-100 mt-2">
            <div className="flex items-start">
              <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-xs">Up to {plan.maxReferrals} referrals</span>
            </div>
            <div className="flex items-start">
              <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-xs">Up to {plan.maxManagers} managers</span>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="pt-0">
        <Button 
          variant={selectedPlanId === plan.id ? 'default' : 'outline'}
          className={`w-full text-sm py-1 h-8 shadow-sm transition-all ${selectedPlanId === plan.id ? colors.button : ''}`}
          onClick={() => onSelectPlan(plan.id, selectedPlanType)}
        >
          {selectedPlanId === plan.id ? 'Selected' : 'Select'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default PlanCard;
